import React from 'react';
import PageHero from '../components/common/PageHero';
import { Section, SectionHeading } from '../components/common/Section';
import BookCall from '../components/common/BookCall';

const VALUE = [
  {
    n: '01',
    title: 'Complements your advice',
    body: 'You hold the structure, strategy and numbers. This addresses whether the family is aligned behind them — the variable most likely to undo good technical work.',
  },
  {
    n: '02',
    title: 'Protects the plan you built',
    body: 'A sound structure still fails when the family pulls in different directions. Surfacing misalignment early protects the integrity of your work.',
  },
  {
    n: '03',
    title: 'Rigorous, not soft',
    body: 'A structured instrument with per-dimension scoring and family-level variance analysis, grounded in Family Firm Institute methodology.',
  },
];

const SIGNALS = [
  'Succession is approaching and the next generation’s readiness is unclear',
  'Unspoken disagreement that never quite surfaces in meetings',
  'A sound structure keeps stalling on family dynamics, not technical detail',
  'The family needs a neutral, structured way to have the conversation they have avoided',
];

const BOUNDARIES = [
  'Does not provide financial, investment, legal or tax advice',
  'Does not compete with your mandate — the family stays your client',
  'Individual responses remain confidential to the family aggregate',
  'Findings inform, never override, professional advice',
];

const ForAdvisorsPage = () => {
  return (
    <div className="bg-cream">
      <PageHero
        eyebrow="For Advisors & Referrers"
        title="When the structure is sound but the family is not aligned"
        subtitle="Wealth managers, attorneys and accountants refer families here when the obstacle is no longer technical."
      />

      {/* Where it fits */}
      <Section>
        <SectionHeading
          eyebrow="Where it fits"
          title="Alongside you — never in front of you"
          intro="Built to strengthen the advisory relationship you already hold, not to dilute it."
        />
        <div className="mt-16 lg:mt-20 border-t border-secondary-200">
          {VALUE.map((v) => (
            <div
              key={v.n}
              className="grid md:grid-cols-12 gap-4 md:gap-10 py-10 border-b border-secondary-200"
            >
              <span className="md:col-span-2 font-serif text-2xl text-secondary-300">
                {v.n}
              </span>
              <h3 className="md:col-span-4 font-serif text-2xl font-normal text-secondary-900 leading-snug">
                {v.title}
              </h3>
              <p className="md:col-span-6 text-secondary-600 leading-relaxed text-lg">
                {v.body}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* When to refer + boundaries */}
      <Section divider className="bg-white">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          <div>
            <p className="text-[11px] font-semibold text-primary-700 uppercase tracking-[0.2em] mb-6">
              When to refer
            </p>
            <h2 className="font-serif text-2xl md:text-3xl font-normal text-secondary-900 leading-snug tracking-tight mb-10">
              The signals are relational, not technical
            </h2>
            <div className="border-t border-secondary-200">
              {SIGNALS.map((s) => (
                <p
                  key={s}
                  className="py-5 border-b border-secondary-200 text-secondary-700"
                >
                  {s}
                </p>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-primary-700 uppercase tracking-[0.2em] mb-6">
              Clear boundaries
            </p>
            <h2 className="font-serif text-2xl md:text-3xl font-normal text-secondary-900 leading-snug tracking-tight mb-10">
              What this is, and is not
            </h2>
            <div className="border-t border-secondary-200">
              {BOUNDARIES.map((b) => (
                <p
                  key={b}
                  className="py-5 border-b border-secondary-200 text-secondary-700"
                >
                  {b}
                </p>
              ))}
            </div>
            <p className="text-xs text-secondary-400 leading-relaxed mt-8">
              The Family Legacy Bridge provides family wealth coaching and does
              not offer financial, investment, or tax advice. Amy Jaffe is not a
              registered Financial Services Provider under the FAIS Act.
            </p>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section divider>
        <div className="max-w-2xl">
          <h2 className="font-serif text-3xl md:text-4xl font-normal text-secondary-900 leading-[1.15] tracking-tight mb-6">
            Discuss a family you have in mind
          </h2>
          <p className="text-lg text-secondary-600 leading-relaxed mb-10">
            A confidential conversation about fit and process — no obligation
            for you or the family.
          </p>
          <BookCall size="lg">Request a conversation</BookCall>
        </div>
      </Section>
    </div>
  );
};

export default ForAdvisorsPage;
